import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { getReferencedLocation } from '~/client/src/shared/resources/resourceLink/registry'
import { getDeletedObjectLabel } from '~/client/src/shared/utils/getDeletedObjectLabel'

import LocationBase from '../../../models/LocationObjects/LocationBase'
import TagsStore from '../../../stores/domain/Tags.store'
import type ILocationDisplayFallback from '../../../types/ILocationDisplayFallback'
import {
  getLocationParentNames,
  LocationReference,
} from '../../ObjectReference/LocationReference'
import { ISharedProps } from '../BaseStruxhubInput'
import BaseStruxhubSelector from './BaseStruxhubSelector'

// localization: no text to translate

interface ISelectedLocationTagProps {
  value: string
  fallback?: ILocationDisplayFallback | null

  tagsStore?: TagsStore
}

/** A selected location as a field shows it: its glyph, its name and, beneath, its LBS. */
@inject('tagsStore')
@observer
export class SelectedLocationTag extends React.Component<ISelectedLocationTagProps> {
  public render() {
    const shown = this.shownLocation
    if (!shown) return null

    return (
      <LocationReference
        iconName={shown.iconName}
        color={shown.color}
        variant="plain"
        size="dense"
        parentNames={shown.parentNames}
        resourceCode={shown.resourceCode}
      >
        <span title={shown.name}>{shown.name}</span>
      </LocationReference>
    )
  }

  private get shownLocation() {
    const { value, fallback, tagsStore } = this.props
    const { tagStoreByTagTypeMap } = tagsStore

    const attribute = getReferencedLocation(value, tagsStore)
    if (attribute) {
      return {
        name: attribute.isDeleted
          ? getDeletedObjectLabel(attribute.name)
          : attribute.name,
        iconName: attribute.iconName,
        color: attribute.color,
        resourceCode: attribute.stxResourceId,
        parentNames: getLocationParentNames(attribute, tagStoreByTagTypeMap),
      }
    }
    if (!fallback) return null

    const parent = fallback.parentId
      ? (tagsStore.getTagById(fallback.parentId) as LocationBase)
      : null
    return {
      name: fallback.name,
      iconName: fallback.iconName,
      color: fallback.color,
      resourceCode: undefined,
      parentNames: parent
        ? [...getLocationParentNames(parent, tagStoreByTagTypeMap), parent.name]
        : [],
    }
  }
}

interface IProps extends ISharedProps {
  onClick?: () => void
  fallback?: ILocationDisplayFallback | null
}

@observer
export default class StruxhubAttributeSelector extends React.Component<IProps> {
  public render() {
    const { value, fallback } = this.props

    return (
      // A min-height rather than the fixed 40px this carried before: a location with
      // ancestors is two lines tall, and the control sits level with its single-line
      // siblings when there are none to show.
      <BaseStruxhubSelector {...this.props} selectorClassName="tw:min-h-10">
        <SelectedLocationTag value={value} fallback={fallback} />
      </BaseStruxhubSelector>
    )
  }
}
